import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Receipt } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const ReceiptCard = ({ receipt }) => (
  <div
    data-testid="dues-receipt-item"
    className="bg-slate-900/60 border border-slate-700 rounded-lg p-3"
  >
    <div className="flex items-center justify-between mb-2">
      <span className="font-semibold text-green-400">
        ${Number(receipt.amount || 0).toFixed(2)}
      </span>
      <span className="text-xs text-slate-400">
        {new Date(receipt.payment_date || receipt.created_at).toLocaleDateString()}
        {" · "}
        {receipt.payment_method || "Square"}
      </span>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {(receipt.months_covered || []).map((m) => (
        <Badge key={m} className="bg-blue-600/80 text-white">
          {m}
        </Badge>
      ))}
    </div>
  </div>
);

export const DuesReceiptPopup = () => {
  const [unseen, setUnseen] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/dues/my-receipts`, authHeaders())
      .then((res) => {
        const list = (res.data?.receipts || []).filter((r) => !r.seen);
        if (list.length > 0) {
          setUnseen(list);
          setOpen(true);
        }
      })
      .catch(() => {});
  }, []);

  const acknowledge = async () => {
    setOpen(false);
    try {
      await axios.post(`${API}/dues/my-receipts/ack`, {}, authHeaders());
    } catch {
      // best-effort
    }
  };

  if (unseen.length === 0) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) acknowledge();
      }}
    >
      <DialogContent
        data-testid="dues-receipt-dialog"
        className="bg-slate-800 border-slate-700 text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Receipt className="w-5 h-5 text-green-400" />
            Dues Payment Received
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Here's exactly which months your payment
            {unseen.length > 1 ? "s" : ""} covered:
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-72 overflow-y-auto">
          {unseen.map((r) => (
            <ReceiptCard key={r.id} receipt={r} />
          ))}
        </div>
        <Button
          data-testid="dues-receipt-ack-btn"
          onClick={acknowledge}
          className="w-full bg-green-600 hover:bg-green-700"
        >
          Got It
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const DuesReceiptHistory = () => {
  const [receipts, setReceipts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/dues/my-receipts`, authHeaders())
      .then((res) => setReceipts(res.data?.receipts || []))
      .catch(() => {});
  }, []);

  if (receipts.length === 0) return null;

  return (
    <div
      data-testid="dues-receipt-history"
      className="bg-slate-900/40 border border-slate-700 rounded-lg p-4"
    >
      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <Receipt className="w-4 h-4 text-green-400" />
        Payment Receipts
      </h3>
      <div className="space-y-2">
        {receipts.slice(0, 5).map((r) => (
          <ReceiptCard key={r.id} receipt={r} />
        ))}
      </div>
    </div>
  );
};
